/**
 * TargetPriceDraftBar — docked row for a live target-price draft: symbol +
 * fill progress (cardProgress, value-based), tap to open TargetPriceDraftSheet.
 * The saved state flips the row to a check; the ✕ dismisses the bar (the card
 * stays in the transcript and can be re-opened from there — the bar is a
 * surface, not the store). ThesisDraftBar's analogue; ChatConversation owns
 * when it renders (Task 12).
 *
 * THE FRACTION IS LABELLED (2026-10-30): cardProgress counts FILLED VALUES
 * (each active scenario's 5 drivers + the 3 probabilities = 18), while the
 * wizard's gate is the 15 confirmation STAMPS — so 「18/18」 next to
 * 「下一步：機率——確認後繼續」 read as "done" when nothing had been confirmed.
 * A second 「已確認 0/15」 counter was rejected: one number, honestly named.
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { cardProgress } from '../../services/targetPriceCard';
import type { TargetPriceCard } from '../../services/targetPriceCard';

export function TargetPriceDraftBar({
  card,
  saved,
  onOpen,
  onDismiss,
}: {
  card: TargetPriceCard;
  saved: boolean;
  onOpen: () => void;
  onDismiss?: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { filled, total } = cardProgress(card);

  return (
    <View
      testID="target-price-draft-bar"
      style={[
        styles.row,
        { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
      ]}
    >
      <Pressable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={`${card.symbol} ${t('chat.targetPrice.draft', {
          defaultValue: 'Target price draft',
        })}`}
        style={styles.main}
      >
        <Text style={[styles.symbol, { color: theme.colors.primary }]}>▣ {card.symbol}</Text>
        {saved ? (
          <Text style={[styles.meta, { color: theme.colors.success }]}>
            ✓ {t('chat.targetPrice.saved', { defaultValue: 'Target price saved' })}
          </Text>
        ) : (
          <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
            {t('chat.targetPrice.draft', { defaultValue: 'Target price draft' })} ◔{' '}
            {t('chat.targetPrice.filledLabel', { defaultValue: 'filled' })} {filled}/{total} ·{' '}
            {t('chat.tapToView', { defaultValue: 'Tap to view' })}
          </Text>
        )}
      </Pressable>
      <Pressable
        testID="target-price-draft-dismiss"
        onPress={onDismiss}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={t('actions.dismiss', { defaultValue: 'Dismiss' })}
        style={styles.dismiss}
      >
        <Text style={[styles.dismissText, { color: theme.colors.textTertiary }]}>✕</Text>
      </Pressable>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      borderTopWidth: 1,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    main: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
    },
    symbol: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    meta: {
      fontSize: theme.typography.fontSizeSm,
      flexShrink: 1,
    },
    dismiss: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    dismissText: {
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
